/* TALO — theme.js
   Vanilla JS, no dependencies, loaded with defer.
   Handles: scroll animations, nav drawer, quantity steppers,
   product variant selection (pack tiles), sticky mobile ATC, localization. */

(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Money -------------------------------------------------------------
     Single source of truth for client-side price strings. Whole-dollar
     amounts drop the trailing ".00" ($51.00 -> $51) to match the Liquid
     side, which pipes money through `replace: '.00', ''`. Genuine cents
     are never touched: $244.99 stays $244.99. */
  function formatMoney(cents) {
    var format = (window.talo && window.talo.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var out = format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    return out.replace(/(\d)\.00(?!\d)/, '$1').replace(/(\d),00(?!\d)/, '$1');
  }

  /* --- Scroll-in animations ------------------------------------------- */
  function initAnimations() {
    var els = document.querySelectorAll('[data-animate]');
    if (!els.length) return;
    if (reducedMotion || !window.talo || !window.talo.animations || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-inview'); });
      return;
    }
    // Anything already on screen at load is shown instantly — content in the
    // viewport must never be invisible.
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var pending = [];
    els.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < vh && rect.bottom > 0) {
        el.classList.add('is-instant');
        el.classList.add('is-inview');
      } else {
        pending.push(el);
      }
    });
    if (!pending.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        // Fast scrolling can land a block deep inside the viewport before this
        // callback runs — those reveal instantly too, never invisibly.
        var vph = window.innerHeight || document.documentElement.clientHeight;
        if (entry.boundingClientRect.top < vph * 0.85) {
          entry.target.classList.add('is-instant');
        }
        entry.target.classList.add('is-inview');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px 200px 0px' });
    pending.forEach(function (el) { io.observe(el); });
  }

  /* --- Buy-box jar: paint the halo only once the image has painted ------ */
  function initBuyboxMedia() {
    document.querySelectorAll('.buybox__jar').forEach(function (box) {
      var img = box.querySelector('img');
      if (!img) return;
      var mark = function () { box.classList.add('is-loaded'); };
      if (img.complete && img.naturalWidth > 0) mark();
      else img.addEventListener('load', mark, { once: true });
    });
  }

  /* --- Overlay + drawers (nav; cart drawer is driven by cart.js) ------- */
  var overlay = null;
  function getOverlay() {
    if (!overlay) overlay = document.querySelector('[data-page-overlay]');
    return overlay;
  }
  function openDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    var ov = getOverlay();
    if (ov) {
      ov.hidden = false;
      requestAnimationFrame(function () { ov.classList.add('is-open'); });
    }
    document.body.style.overflow = 'hidden';
    var focusable = drawer.querySelector('button, a, input, [tabindex]');
    if (focusable) focusable.focus();
  }
  function closeDrawers() {
    document.querySelectorAll('.nav-drawer.is-open, .cart-drawer.is-open').forEach(function (d) {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    });
    var ov = getOverlay();
    if (ov) {
      ov.classList.remove('is-open');
      window.setTimeout(function () { ov.hidden = true; }, 300);
    }
    document.body.style.overflow = '';
  }
  window.taloDrawers = { open: openDrawer, closeAll: closeDrawers };

  function initNavDrawer() {
    var drawer = document.querySelector('[data-nav-drawer]');
    if (!drawer) return;
    document.querySelectorAll('[data-nav-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { openDrawer(drawer); });
    });
  }

  /* Delegated so close buttons keep working after the cart drawer body is
     replaced by a Section Rendering refresh. */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-drawer-close]')) { closeDrawers(); return; }
    if (e.target.closest('[data-page-overlay]')) closeDrawers();
  });

  /* --- PDP gallery lightbox: tiles may crop, the lightbox never does ----- */
  function lightboxEl() { return document.querySelector('[data-lightbox]'); }
  function closeLightbox() {
    var lb = lightboxEl();
    if (!lb) return;
    lb.classList.remove('is-open');
    lb.hidden = true;
    document.body.style.overflow = '';
  }
  document.addEventListener('click', function (e) {
    var tile = e.target.closest('[data-lightbox-src]');
    if (tile) {
      var lb = lightboxEl();
      if (!lb) return;
      var img = lb.querySelector('[data-lightbox-img]');
      if (!img) {
        img = document.createElement('img');
        img.setAttribute('data-lightbox-img', '');
        lb.appendChild(img);
      }
      img.src = tile.getAttribute('data-lightbox-src');
      img.alt = tile.getAttribute('data-lightbox-alt') || '';
      lb.hidden = false;
      lb.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      return;
    }
    var lb2 = e.target.closest('[data-lightbox]');
    if (lb2 && (e.target === lb2 || e.target.closest('[data-lightbox-close]') || e.target.tagName === 'IMG')) {
      closeLightbox();
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeLightbox();
  });

  /* --- Buy-box sticky bar: appears once the buy box scrolls out of view -- */
  function initBuybar() {
    var bar = document.querySelector('[data-buybar]');
    var box = document.querySelector('[data-buybox]');
    if (!bar || !box || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      });
    }, { rootMargin: '0px' });
    io.observe(box);
    var cta = bar.querySelector('[data-buybar-cta]');
    if (cta) {
      cta.addEventListener('click', function (e) {
        e.preventDefault();
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }
  initBuybar();
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawers();
  });

  /* --- Header dropdown nav (hover is CSS; click/touch/keyboard here) ----
     R16: at <=749px the nav strip (.hdr__nav-inner) is a horizontal scroll
     container (R6-1 rules) and clips the absolutely-positioned panel. On
     mobile the open panel is position:fixed (CSS) and anchored below the
     toggle here; a page scroll closes it so it can never sit detached.
     Scrolls that originate inside the strip (horizontal pan) or inside the
     panel itself are ignored — the strip keeps its R6-1 behavior. */
  var mobileNavMQ = window.matchMedia('(max-width: 749px)');
  function syncDropdownPanel(dropdown) {
    var panel = dropdown.querySelector('.hdr__dropdown-panel');
    if (!panel) return;
    if (mobileNavMQ.matches && dropdown.hasAttribute('data-open')) {
      var rect = dropdown.getBoundingClientRect();
      panel.style.top = Math.round(rect.bottom + 8) + 'px';
    } else {
      panel.style.top = '';
    }
  }
  function closeDropdowns(except) {
    document.querySelectorAll('[data-dropdown][data-open]').forEach(function (d) {
      if (d === except) return;
      d.removeAttribute('data-open');
      var btn = d.querySelector('.hdr__dropdown-toggle');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      var panel = d.querySelector('.hdr__dropdown-panel');
      if (panel) panel.style.top = '';
    });
  }
  document.addEventListener('scroll', function (e) {
    if (!mobileNavMQ.matches) return;
    if (e.target && e.target.closest && (e.target.closest('.hdr__dropdown-panel') || e.target.closest('.hdr__nav-inner'))) return;
    if (document.querySelector('[data-dropdown][data-open]')) closeDropdowns();
  }, { capture: true, passive: true });
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('.hdr__dropdown-toggle');
    if (toggle) {
      var dropdown = toggle.closest('[data-dropdown]');
      var isOpen = dropdown.hasAttribute('data-open');
      closeDropdowns(dropdown);
      if (isOpen) {
        dropdown.removeAttribute('data-open');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        dropdown.setAttribute('data-open', '');
        toggle.setAttribute('aria-expanded', 'true');
      }
      syncDropdownPanel(dropdown);
      return;
    }
    if (!e.target.closest('[data-dropdown]')) closeDropdowns();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDropdowns();
  });

  /* --- Quantity steppers (event delegation; works after re-renders) ---- */
  document.addEventListener('click', function (e) {
    var minus = e.target.closest('[data-qty-minus]');
    var plus = e.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;
    var wrap = (minus || plus).closest('[data-qty]');
    var input = wrap && wrap.querySelector('[data-qty-input]');
    if (!input) return;
    var value = parseInt(input.value, 10) || 0;
    var min = parseInt(input.min, 10);
    value = plus ? value + 1 : value - 1;
    if (!isNaN(min)) value = Math.max(min, value);
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* --- Buy box: pack tiles + native selling-plan choice --------------------
     Prices always come from server-rendered variant/selling-plan data
     (Appstle selling plans are the source of truth — nothing is computed
     from a hardcoded discount). */
  function initBuyBoxes() {
    document.querySelectorAll('[data-buybox]').forEach(function (box) {
      var dataEl = box.querySelector('[data-buybox-json]');
      if (!dataEl) return;
      var data;
      try { data = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var idInput = box.querySelector('input[name="id"]');
      var planInput = box.querySelector('[data-selling-plan-input]');
      var freqWrap = box.querySelector('[data-freq-wrap]');
      var freqSelect = box.querySelector('[data-freq-select]');
      var atcBtn = box.querySelector('[data-atc-button]');
      var atcPrice = box.querySelector('[data-atc-price]');

      function currentVariant() {
        var checked = box.querySelector('[data-variant-radio]:checked');
        var id = checked ? checked.value : (idInput && idInput.value);
        return data.variants.find(function (v) { return String(v.id) === String(id); });
      }

      function update() {
        var variant = currentVariant();
        if (!variant) return;
        if (idInput) idInput.value = variant.id;

        var subscribed = false;
        var purchase = box.querySelector('[data-purchase-option]:checked');
        if (purchase && purchase.value === 'subscribe') subscribed = true;

        var price = variant.price;
        var suffix = '';
        if (subscribed && freqSelect && planInput) {
          var planId = freqSelect.value;
          var allocation = (variant.plans || []).find(function (p) { return String(p.planId) === String(planId); });
          if (allocation) {
            price = allocation.price;
            suffix = data.subscribeSuffix ? ' ' + data.subscribeSuffix : '';
          }
          planInput.value = planId;
          planInput.disabled = false;
        } else if (planInput) {
          planInput.value = '';
          planInput.disabled = true;
        }
        if (freqWrap) freqWrap.hidden = !subscribed;

        if (atcBtn) {
          if (variant.available) {
            atcBtn.disabled = false;
            atcBtn.innerHTML = (atcBtn.getAttribute('data-atc-prefix') || window.talo.strings.addToCart) +
              ' — <span data-atc-price>' + formatMoney(price) + '</span>' + suffix;
          } else {
            atcBtn.disabled = true;
            atcBtn.textContent = window.talo.strings.soldOut;
          }
        }
      }

      box.querySelectorAll('[data-variant-radio], [data-purchase-option]').forEach(function (input) {
        input.addEventListener('change', update);
      });
      if (freqSelect) freqSelect.addEventListener('change', update);
      update();
    });
  }

  /* --- Product form: pack tiles / variant selection --------------------- */
  function initProductForms() {
    document.querySelectorAll('[data-product-wrapper]').forEach(function (wrapper) {
      var dataEl = wrapper.querySelector('[data-variant-json]');
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var idInput = wrapper.querySelector('input[name="id"]');
      var priceEl = wrapper.querySelector('[data-price]');
      var priceCompareEl = wrapper.querySelector('[data-price-compare]');
      var mathEl = wrapper.querySelector('[data-unit-math-slot]');
      var atcBtn = wrapper.querySelector('[data-atc-button]');
      var stickyVariant = document.querySelector('[data-sticky-variant]');
      var stickyPrice = document.querySelector('[data-sticky-price]');
      var planInput = wrapper.querySelector('[data-selling-plan-input]');
      var freqWrap = wrapper.querySelector('[data-freq-wrap]');
      var freqSelect = wrapper.querySelector('[data-freq-select]');

      function findVariant(id) {
        return variants.find(function (v) { return String(v.id) === String(id); });
      }

      function currentPlanState(variant) {
        /* Selling-plan prices come from server-rendered allocation data
           (Appstle is the source of truth) — never computed client-side. */
        var subscribed = false;
        var purchase = wrapper.querySelector('[data-purchase-option]:checked');
        if (purchase && purchase.value === 'subscribe') subscribed = true;
        var allocation = null;
        if (subscribed && freqSelect) {
          allocation = (variant.plans || []).find(function (p) {
            return String(p.planId) === String(freqSelect.value);
          });
        }
        return { subscribed: subscribed && !!allocation, allocation: allocation };
      }

      function updateVariant(id) {
        var variant = findVariant(id);
        if (!variant || !idInput) return;
        idInput.value = variant.id;
        var plan = currentPlanState(variant);
        if (planInput) {
          planInput.value = plan.subscribed ? String(freqSelect.value) : '';
          planInput.disabled = !plan.subscribed;
        }
        if (freqWrap) {
          var purchase = wrapper.querySelector('[data-purchase-option]:checked');
          freqWrap.hidden = !(purchase && purchase.value === 'subscribe');
        }
        if (priceCompareEl) {
          priceCompareEl.hidden = !plan.subscribed;
          if (plan.subscribed) priceCompareEl.textContent = formatMoney(variant.price);
        }
        if (priceEl) priceEl.textContent = formatMoney(plan.subscribed ? plan.allocation.price : variant.price);
        if (mathEl) {
          mathEl.textContent = variant.unit_math || '';
          mathEl.hidden = !variant.unit_math;
        }
        if (atcBtn) {
          if (variant.available) {
            atcBtn.disabled = false;
            atcBtn.textContent = window.talo.strings.addToCart;
          } else {
            atcBtn.disabled = true;
            atcBtn.textContent = window.talo.strings.soldOut;
          }
        }
        if (stickyVariant) stickyVariant.textContent = variant.title;
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        var bisBlocks = wrapper.querySelectorAll('[data-back-in-stock]');
        bisBlocks.forEach(function (b) { b.hidden = variant.available; });
        if (window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
        wrapper.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant } }));
      }

      wrapper.querySelectorAll('[data-variant-radio]').forEach(function (radio) {
        radio.addEventListener('change', function () { updateVariant(radio.value); });
      });
      wrapper.querySelectorAll('[data-purchase-option]').forEach(function (radio) {
        radio.addEventListener('change', function () { updateVariant(idInput.value); });
      });
      if (freqSelect) {
        freqSelect.addEventListener('change', function () { updateVariant(idInput.value); });
      }
    });
  }

  /* --- Sticky mobile ATC -------------------------------------------------- */
  function initStickyAtc() {
    var bar = document.querySelector('[data-sticky-atc]');
    var sentinel = document.querySelector('[data-atc-button]');
    if (!bar || !sentinel || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(sentinel);
    var stickyBtn = bar.querySelector('[data-sticky-add]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        var mainBtn = document.querySelector('[data-atc-button]');
        if (mainBtn && !mainBtn.disabled) mainBtn.click();
      });
    }
  }

  /* --- Article: table of contents + inline product module ----------------- */
  function initArticle() {
    var content = document.querySelector('[data-article-content]');
    if (!content) return;

    var headings = content.querySelectorAll('h2');
    var toc = document.querySelector('[data-article-toc]');
    var tocList = document.querySelector('[data-article-toc-list]');
    if (toc && tocList && headings.length > 1) {
      headings.forEach(function (h, i) {
        if (!h.id) {
          h.id = 'section-' + (i + 1) + '-' + h.textContent.trim().toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 60);
        }
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + h.id;
        a.textContent = h.textContent;
        li.appendChild(a);
        tocList.appendChild(li);
      });
      toc.hidden = false;
    }

    var module = document.querySelector('[data-article-product-module]');
    if (module && headings.length >= 2) {
      headings[1].parentNode.insertBefore(module, headings[1]);
    }
  }

  /* --- Customer address country/province selects --------------------------- */
  function initAddressSelects() {
    document.querySelectorAll('[data-address-country-select]').forEach(function (countrySelect) {
      var formId = countrySelect.getAttribute('data-form-id');
      var provinceSelect = document.getElementById('AddressProvince-' + formId);
      var provinceContainer = document.getElementById('AddressProvinceContainer-' + formId);
      if (!provinceSelect || !provinceContainer) return;

      function updateProvinces() {
        var option = countrySelect.options[countrySelect.selectedIndex];
        var provinces = [];
        try { provinces = JSON.parse(option.getAttribute('data-provinces') || '[]'); } catch (e) { /* noop */ }
        provinceSelect.innerHTML = '';
        if (!provinces.length) {
          provinceContainer.style.display = 'none';
          return;
        }
        provinces.forEach(function (p) {
          var opt = document.createElement('option');
          opt.value = p[0];
          opt.textContent = p[1];
          provinceSelect.appendChild(opt);
        });
        var defaultProvince = provinceSelect.getAttribute('data-default');
        if (defaultProvince) provinceSelect.value = defaultProvince;
        provinceContainer.style.display = '';
      }

      var defaultCountry = countrySelect.getAttribute('data-default');
      if (defaultCountry) countrySelect.value = defaultCountry;
      countrySelect.addEventListener('change', updateProvinces);
      updateProvinces();
    });
  }

  /* --- Localization / country selector ------------------------------------ */
  function initLocalization() {
    document.querySelectorAll('[data-localization-form] select').forEach(function (select) {
      select.addEventListener('change', function () {
        select.closest('form').submit();
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAnimations();
    initBuyboxMedia();
    initNavDrawer();
    initBuyBoxes();
    initProductForms();
    initStickyAtc();
    initArticle();
    initAddressSelects();
    initLocalization();
  });
})();